"use client"

import { WarningMarkIcon } from "@/app/icons/warning-mark"
import { PriceQuote } from "@/app/offers/price-quote"
import { useOfferValidation } from "@/app/offers/use-offer-validation"
import { PriceInput } from "@/app/price/price-input"
import { useEvmItemOffer } from "@/app/sdk/action-molecules/evm/evm-item-offer-molecule"
import type { Nft } from "@anaconda/core/nft/types"
import { Trans } from "@lingui/react/macro"
import {
  Button,
  Card,
  DialogHeader,
  DialogTitle,
} from "@rarible_int/anaconda_ui_components"
import { atom } from "@reatom/core"
import { reatomComponent } from "@reatom/react"
import { useMemo } from "react"
import {
  useNativeCurrency,
  useWrappedCurrency,
} from "../../blockchains/use-blockchain"
import { ItemPreview } from "./item-preview"

interface Props {
  nft: Nft
  onOpenChange: (isOpened: boolean) => void
}

// An item bid takes exactly one token.
const QUANTITY = 1

export const EvmItemOfferDialogContent = reatomComponent<Props>(
  ({ nft, onOpenChange }) => {
    // The entry point only renders on chains with a wrapped currency, and the
    // SDK only signs item bids in it — no native fallback.
    const currency = useWrappedCurrency(nft.collection.blockchain)
    const nativeCurrency = useNativeCurrency(nft.collection.blockchain)
    const { mutate } = useEvmItemOffer(nft)

    const { priceAtom } = useMemo(() => {
      const priceAtom = atom<number | undefined>(undefined, "evmItemOfferPrice")

      return { priceAtom }
    }, [])

    const price = priceAtom()
    const setPrice = (value: number | undefined) => priceAtom.set(value)
    const { error } = useOfferValidation({
      price,
      quantity: QUANTITY,
      currency,
    })

    return (
      <form
        className="flex w-full min-w-0 flex-col gap-y-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (price === undefined) return
          mutate({ price, currency: currency.id })
          onOpenChange(false)
        }}
      >
        <DialogHeader className="pb-2">
          <DialogTitle className="text-center text-lg">
            <Trans>Make offer</Trans>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <ItemPreview nft={nft} />
          <PriceInput
            onPriceChange={setPrice}
            error={error}
            currency={currency}
          />
          <Card className="flex items-center gap-2 p-4">
            <div>
              <WarningMarkIcon className="flex-shrink-0 flex-grow-0" />
            </div>
            <div>
              <Trans>
                If your {currency.abbreviation.toUpperCase()} balance is short,
                your {nativeCurrency.abbreviation.toUpperCase()} will be swapped
                to {currency.abbreviation.toUpperCase()} first. You'll be asked
                to sign a transaction.
              </Trans>
            </div>
          </Card>
          <PriceQuote
            quantity={QUANTITY}
            price={{ amount: price?.toString() ?? "0", currency }}
          />
          <p className="px-1 text-foreground/60 text-xs">
            <Trans>
              Your {currency.abbreviation} stays in your wallet, approved to the
              exchange, until the owner accepts or you cancel. Offers expire in
              30 days.
            </Trans>
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            className="flex-grow"
            variant="secondary"
            type="button"
            onClick={() => onOpenChange(false)}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            className="flex-grow"
            type="submit"
            disabled={!price || !!error}
          >
            <Trans>Make offer</Trans>
          </Button>
        </div>
      </form>
    )
  },
  "EvmItemOfferDialogContent",
)
